import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { isServicePackageActive } from '../../services/servicePackageService';
import StyledSwitch from '../common/StyledSwitch';
import {
  getServicePackageExpiryLabel,
  getServicePackageName,
  getServicePackageProfessionalNames,
  getServicePackageServiceNames,
} from './servicePackageDisplay';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const DetailLine = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const ServicePackageCard = ({
  servicePackage,
  currency,
  services,
  teamMembers,
  loading,
  canEdit,
  onEdit,
  onToggleStatus,
}) => {
  const { t, i18n } = useTranslation();
  const active = isServicePackageActive(servicePackage.status);
  const statusActionLabel = active
    ? t('servicePackagesManagement.actions.deactivate')
    : t('servicePackagesManagement.actions.activate');
  const serviceNames = getServicePackageServiceNames(servicePackage.serviceIds, services, i18n.language, t);
  const professionalNames = getServicePackageProfessionalNames(servicePackage.teamMemberIds, teamMembers);

  const renderActions = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
      <Tooltip title={t('servicePackagesManagement.actions.edit')}>
        <IconButton
          id={`service-package-edit-${servicePackage.id}`}
          size="small"
          onClick={onEdit}
          disabled={loading}
          aria-label={t('servicePackagesManagement.actions.edit')}
          sx={{ ...actionButtonSx, color: '#1976d2' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={statusActionLabel}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: { xs: 40, sm: 34 } }}>
          <StyledSwitch
            id={`service-package-status-${servicePackage.id}`}
            checked={active}
            onChange={onToggleStatus}
            disabled={loading}
            inputProps={{ 'aria-label': statusActionLabel }}
          />
        </Box>
      </Tooltip>
    </Box>
  );

  return (
    <Card
      id={`service-package-card-${servicePackage.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        opacity: active ? 1 : 0.85,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3, wordBreak: 'break-word' }}>
          {getServicePackageName(servicePackage.name, i18n.language, t)}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DetailLine
            label={t('servicePackagesManagement.card.includedServices')}
            value={serviceNames.join(', ') || '—'}
          />
          <DetailLine
            label={t('servicePackagesManagement.card.professionals')}
            value={professionalNames.length > 0
              ? professionalNames.join(', ')
              : t('servicePackagesManagement.card.anyProfessional')}
          />
        </Box>

        <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: canEdit ? 0.75 : 0 }}>
            <Chip
              size="small"
              label={t('servicePackagesManagement.card.quantity', { count: servicePackage.quantity })}
              sx={{ backgroundColor: brandAlpha('accent', 0.12), color: BRAND.accentText, fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={getServicePackageExpiryLabel(servicePackage.expiresInMonths, t)}
              sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
            />
            <Chip
              size="small"
              label={active
                ? t('servicePackagesManagement.status.active')
                : t('servicePackagesManagement.status.inactive')}
              sx={{
                backgroundColor: active ? '#e8f5e9' : '#ffebee',
                color: active ? '#2e7d32' : '#c62828',
              }}
            />
        </Box>

        {canEdit ? (
          <Box sx={{ display: 'flex' }}>
            {renderActions()}
          </Box>
        ) : null}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
            pt: 1.5,
            mt: 1.5,
            borderTop: `1px solid ${BRAND.line}`,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('servicePackagesManagement.card.price')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {formatCurrency(servicePackage.price, currency || servicePackage.currency)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {t('servicePackagesManagement.card.activeHolders')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {servicePackage.activeHolderCount ?? 0}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ServicePackageCard;
